'use client';

import { useEffect, useRef } from 'react';
import type { Locale } from '../HomeLanding';

interface Resource {
  id: string;
  code: string;
  title: string;
  format: string;
  size: string;
  description: string;
  tags: string[];
  downloadLabel: string;
}

const baseResources: Resource[] = [
  {
    id: 'do-checklist',
    code: 'RES-01',
    title: 'Dissolved Oxygen Monitoring Checklist',
    format: 'PDF',
    size: '2 pages',
    description:
      'Field-ready 24-point checklist covering probe calibration, stratification sampling depth, emergency threshold triggers, and aeration equipment response protocol. Validated across 40+ pond operations.',
    tags: ['Pond', 'RAS', 'Cage', 'All Species'],
    downloadLabel: 'Download Checklist - Free',
  },
  {
    id: 'stocking-calc',
    code: 'RES-02',
    title: 'Stocking Density Calculator',
    format: 'Excel',
    size: '1 spreadsheet',
    description:
      'Biomass-based stocking density calculator for pond and cage systems. Inputs: target harvest weight, survival rate, FCR, water volume, and oxygen supply. Outputs: initial stocking number and monthly biomass projection.',
    tags: ['Tilapia', 'Shrimp', 'Pangasius', 'Trout'],
    downloadLabel: 'Download Calculator - Free',
  },
  {
    id: 'biosec-template',
    code: 'RES-03',
    title: 'Biosecurity Audit Template',
    format: 'PDF + Word',
    size: '8 pages',
    description:
      'Sample biosecurity audit template drawn from our 48-point field protocol. Covers intake water, personnel movement, equipment sanitation, and disease response triggers. Used in ASC pre-certification assessments.',
    tags: ['Shrimp', 'Salmon', 'Tilapia', 'All Systems'],
    downloadLabel: 'Download Template - Free',
  },
];

export default function FreeResources({ locale = 'en' }: { locale?: Locale }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isIndonesian = locale === 'id';
  const isVietnamese = locale === 'vn';
  const isChinese = locale === 'cn';

  const resources: Resource[] =
    locale === 'id'
      ? [
          {
            id: 'do-checklist',
            code: 'RES-01',
            title: 'Checklist Pemantauan Oksigen Terlarut (DO)',
            format: 'PDF',
            size: '2 halaman',
            description:
              'Daftar periksa 24 poin siap pakai, mencakup kalibrasi probe, stratifikasi kedalaman sampling, hingga protokol darurat aerasi. Telah tervalidasi di lebih dari 40 operasional kolam budidaya.',
            tags: ['Kolam', 'RAS', 'Kandang', 'Semua spesies'],
            downloadLabel: 'Unduh Gratis (PDF)',
          },
          {
            id: 'stocking-calc',
            code: 'RES-02',
            title: 'Kalkulator Densitas Tebar & Biomassa',
            format: 'Excel',
            size: '1 spreadsheet',
            description:
              'Alat bantu berbasis Excel untuk menghitung input bobot panen, SR (Survival Rate), FCR, dan suplai oksigen. Menghasilkan proyeksi jumlah tebar awal dan estimasi biomassa bulanan secara akurat.',
            tags: ['Nila', 'Udang', 'Patin', 'Trout'],
            downloadLabel: 'Unduh Gratis (Excel)',
          },
          {
            id: 'biosec-template',
            code: 'RES-03',
            title: 'Template Audit Biosekuriti Mandiri',
            format: 'PDF + Word',
            size: '8 halaman',
            description:
              'Panduan audit 48 poin berdasarkan protokol lapangan profesional. Mencakup sanitasi peralatan, kontrol akses personel, hingga manajemen risiko penyakit. Sesuai untuk persiapan pra-sertifikasi ASC.',
            tags: ['Udang', 'Salmon', 'Nila', 'Semua sistem'],
            downloadLabel: 'Unduh Gratis (PDF)',
          },
        ]
    : locale === 'vn'
        ? [
            {
              id: 'do-checklist',
              code: 'RES-01',
              title: 'Danh sách kiểm tra oxy hòa tan',
              format: 'PDF',
              size: '2 trang',
              description:
                'Danh sách kiểm tra thực địa 24 mục, bao gồm hiệu chuẩn đầu dò, độ sâu lấy mẫu phân tầng, ngưỡng cảnh báo khẩn cấp và quy trình ứng phó thiết bị sục khí. Đã được xác thực trên hơn 40 hoạt động ao nuôi.',
              tags: ['Ao', 'RAS', 'Lồng bè', 'Mọi loài'],
              downloadLabel: 'Tải danh sách kiểm tra - Miễn phí',
            },
            {
              id: 'stocking-calc',
              code: 'RES-02',
              title: 'Máy tính mật độ thả nuôi & sinh khối',
              format: 'Excel',
              size: '1 bảng tính',
              description:
                'Công cụ dựa trên Excel để tính đầu vào trọng lượng thu hoạch, SR (Survival Rate), FCR và nguồn cung oxy. Tạo dự báo số lượng thả ban đầu và ước tính sinh khối hàng tháng một cách chính xác.',
              tags: ['Cá rô phi', 'Tôm', 'Cá tra', 'Cá hồi'],
              downloadLabel: 'Tải miễn phí (Excel)',
            },
            {
              id: 'biosec-template',
              code: 'RES-03',
              title: 'Mẫu kiểm toán an toàn sinh học tự thực hiện',
              format: 'PDF + Word',
              size: '8 trang',
              description:
                'Hướng dẫn kiểm toán 48 mục dựa trên giao thức thực địa chuyên nghiệp. Bao gồm vệ sinh thiết bị, kiểm soát ra vào của nhân sự và quản lý rủi ro dịch bệnh. Phù hợp cho giai đoạn chuẩn bị trước chứng nhận ASC.',
              tags: ['Tôm', 'Cá hồi', 'Cá rô phi', 'Mọi hệ thống'],
              downloadLabel: 'Tải miễn phí (PDF)',
            },
          ]
        : locale === 'cn'
          ? [
              {
                id: 'do-checklist',
                code: 'RES-01',
                title: '溶解氧监测清单',
                format: 'PDF',
                size: '2页',
                description:
                  '适用于现场的 24 项清单，涵盖探头校准、分层采样深度、紧急阈值触发和增氧设备响应流程。已在 40+ 个养殖场验证。',
                tags: ['池塘', 'RAS', '网箱', '所有物种'],
                downloadLabel: '下载清单 - 免费',
              },
              {
                id: 'stocking-calc',
                code: 'RES-02',
                title: '养殖密度与生物量计算器',
                format: 'Excel',
                size: '1 个工作表',
                description:
                  '基于 Excel 的工具，用于计算收获体重、SR（存活率）、FCR 和供氧需求。生成初始放养数量和每月生物量预测。',
                tags: ['罗非鱼', '虾', '鲶鱼', '鳟鱼'],
                downloadLabel: '免费下载（Excel）',
              },
              {
                id: 'biosec-template',
                code: 'RES-03',
                title: '生物安全审计模板',
                format: 'PDF + Word',
                size: '8 页',
                description:
                  '依据 48 项现场流程编制的样板生物安全审计模板。涵盖进水、人员流动、设备消毒以及疾病响应触发条件，适用于 ASC 预认证评估。',
                tags: ['虾', '三文鱼', '罗非鱼', '所有系统'],
                downloadLabel: '免费下载（PDF）',
              },
            ]
          : baseResources;

  useEffect(() => {
    if (!sectionRef.current) return;
    const items = sectionRef.current.querySelectorAll('.res-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const i = Array.from(items).indexOf(entry.target as Element);
            setTimeout(() => entry.target.classList.add('active'), i * 100);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="resources"
      ref={sectionRef}
      className="py-24 bg-canvas scroll-mt-20"
      aria-label={isVietnamese ? 'Tài liệu miễn phí' : isChinese ? '免费资料' : 'Free resources'}
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4 res-reveal reveal">
              <span className="inline-block w-6 h-px bg-blue" />
              <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
                {isVietnamese
                  ? 'Tài liệu miễn phí'
                  : isIndonesian
                    ? 'Sumber Daya Teknis Gratis.'
                  : isChinese
                      ? '免费技术资源。'
                      : 'Ungated Resources'}
              </span>
            </div>
            <h2
              className="res-reveal reveal reveal-delay-1 font-bold tracking-[-0.04em] text-charcoal leading-tight"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              {isVietnamese ? 'Chứng minh trước khi chúng tôi hỏi' : isIndonesian ? 'Sumber Daya Teknis Gratis.' : isChinese ? '先证明，再留邮箱' : 'Prove it before we ask'}
              <br />
              <span className="text-titanium font-light">
                {isVietnamese ? 'email của bạn.' : isIndonesian ? 'Unduh dan gunakan alat bantu kerja kami sekarang.' : isChinese ? '再给我们邮箱。' : 'for your email.'}
              </span>
            </h2>
          </div>
          <p className="res-reveal reveal reveal-delay-2 text-titanium text-[15px] leading-relaxed max-w-sm">
            {isVietnamese
              ? 'Ba tài liệu làm việc - không form, không chặn, không chuỗi theo dõi. Hãy lấy và sử dụng chúng.'
              : isIndonesian
                ? 'Tiga instrumen kerja profesional — tanpa pengisian formulir, tanpa biaya, dan tanpa tindak lanjut otomatis. Solusi praktis untuk operasional harian Anda.'
                : isChinese
                  ? '三份专业工作资料——无需填写表单、免费提供，也不会自动跟进。为日常运营提供实用解决方案。'
                  : 'Three working documents - no form, no gate, no follow-up sequence. Take them and use them.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {resources.map((res, index) => (
            <div
              key={res.id}
              className="res-reveal reveal bg-surface border border-divider rounded-md p-7 flex flex-col hover:shadow-card-hover hover:border-blue/30 transition-all duration-300 group"
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded flex items-center justify-center bg-blue-light text-blue">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="12" y1="18" x2="12" y2="12" />
                      <polyline points="9 15 12 18 15 15" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-mono text-[10px] text-titanium tracking-[0.12em] block">{res.code}</span>
                    <span className="font-mono text-[10px] text-blue font-medium">
                      {res.format} · {res.size}
                    </span>
                  </div>
                </div>
              </div>

              <h3 className="font-semibold text-charcoal text-[17px] tracking-tight leading-snug mb-3">
                {res.title}
              </h3>

              <p className="text-titanium text-[13px] leading-relaxed flex-1 mb-5">{res.description}</p>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {res.tags.map((tag) => (
                  <span
                    key={tag}
                    className="font-mono text-[10px] text-titanium bg-surface-alt border border-divider px-2 py-0.5 rounded-sm tracking-wide"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <button className="w-full flex items-center justify-between px-5 py-3 border border-blue text-blue rounded text-[13px] font-semibold hover:bg-blue hover:text-white transition-colors duration-200 group-hover:shadow-blue-glow">
                <span>{res.downloadLabel}</span>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2v8M4 7l4 4 4-4M2 13h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}



